/**
 * Mirrors the cart's product flags onto the `Product flags` cart attribute.
 *
 * Reads its state from the node `cart-product-flags.liquid` renders rather than deriving it
 * in JS, so classification lives in one place.
 */
export default () => {
  // Writes are chained rather than concurrent: two cart mutations landing together would
  // otherwise race, and the loser would leave a stale value on the cart.
  let queue = Promise.resolve();
  let outstanding = 0;

  /**
   * @param {string} desired
   * @returns {Promise<boolean>} whether the write landed; never rejects
   */
  const write = async (desired) => {
    try {
      // A stalled request would otherwise never settle, and the chain below would never
      // advance again — every later sync would queue behind a promise that never resolves.
      const signal =
        typeof AbortSignal !== 'undefined' && AbortSignal.timeout
          ? AbortSignal.timeout(8000)
          : undefined;

      const response = await fetch(
        `${window.Shopify.routes.root}cart/update.js`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ attributes: { 'Product flags': desired } }),
          signal,
        }
      );

      if (!response.ok) {
        console.error(
          '[cartProductFlags] sync rejected',
          response.status,
          desired
        );
        return false;
      }

      return true;
    } catch (error) {
      // Caught, never rethrown: the checkout handler awaits this chain, and a rejection
      // would leave the shopper on a link that no longer navigates.
      console.error('[cartProductFlags] sync failed', desired, error);
      return false;
    }
  };

  /** Queues a write when the rendered state disagrees with the stored attribute. */
  const sync = () => {
    const flag = document.getElementById('cart-product-flags');
    if (!flag) {
      console.warn(
        '[cartProductFlags] #cart-product-flags missing; sync skipped'
      );
      return;
    }

    const desired = flag.dataset.productFlags;
    if (!desired) {
      console.warn(
        '[cartProductFlags] data-product-flags missing; sync skipped'
      );
      return;
    }

    const stored = flag.dataset.productFlagsAttribute;
    if (stored === desired) return;
    // Nothing to correct on an empty cart that has never carried the attribute.
    if (flag.dataset.isEmpty === 'true' && !stored) return;

    outstanding += 1;
    queue = queue
      .then(() => write(desired))
      .then((landed) => {
        if (!landed) return;
        // Re-queried rather than closed over: a cart re-render between queueing and
        // landing detaches the node this ran for. Keeps a later sync from repeating a
        // write that already succeeded.
        const live = document.getElementById('cart-product-flags');
        if (live) live.dataset.productFlagsAttribute = desired;
      })
      .catch((error) => {
        console.error('[cartProductFlags] queue step failed', desired, error);
      })
      .finally(() => {
        outstanding -= 1;
      });
  };

  /**
   * Intercepts primary clicks on the checkout link, waits for any pending write, then
   * navigates. Navigation happens whatever the write did.
   * @param {MouseEvent} e
   */
  const handleCheckoutClick = async (e) => {
    if (!(e.target instanceof Element)) return;
    if (e.defaultPrevented) return;
    // Leave new-tab and non-primary clicks to the browser.
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) {
      return;
    }

    const link = e.target.closest('a[href="/checkout"]');
    if (!link) return;

    sync();
    if (outstanding === 0) return;

    e.preventDefault();
    try {
      const outcome = await Promise.race([
        queue.then(() => 'synced'),
        new Promise((resolve) => setTimeout(() => resolve('timeout'), 1500)),
      ]);
      if (outcome === 'timeout') {
        console.warn(
          '[cartProductFlags] checkout proceeded before sync landed'
        );
      }
    } finally {
      window.location.href = link.href;
    }
  };

  // Both events must stay bound: either can be the only one that fires for a given cart
  // mutation. Registered before the first sync so a throw there cannot cost us them.
  document.addEventListener('cart:update', sync);
  document.addEventListener('main-cart:update', sync);
  document.addEventListener('click', handleCheckoutClick);

  sync();
};
